import type { TrailerBodyStyle, TruckBodyStyle, TyreLayoutAxleRole, TyreLayoutSubjectKind } from '@chukua/common';
import { TRAILER_STYLE_DIMENSIONS, TRUCK_STYLE_DIMENSIONS, isTruckBodyStyle, type VehicleBodyStyle } from './vehicle-body-styles';

/** Metres throughout: x across the vehicle (driver's left negative), depth from the front bumper back, up from the ground. */
export interface TyreSize {
  radius: number;
  width: number;
  /** Between the tyres of a dual pair, or of a line axle's neighbours. */
  gap: number;
  /** From the centreline to the outer face of the outermost tyre. */
  outerHalfWidth: number;
}

export interface PlacedTyre {
  code: string;
  name: string;
  /** Centre of the tyre across the vehicle. */
  x: number;
  side: 'left' | 'middle' | 'right';
  /** The tyre whose face the viewer sees: the outer one of each side. */
  isOuter: boolean;
  radius: number;
  width: number;
}

export interface PlacedAxle {
  role: TyreLayoutAxleRole;
  depth: number;
  size: TyreSize;
  tyres: PlacedTyre[];
}

export interface VehicleLayout {
  kind: TyreLayoutSubjectKind;
  bodyStyle: VehicleBodyStyle;
  length: number;
  axles: PlacedAxle[];
}

export interface VehicleAxleInput {
  role: TyreLayoutAxleRole;
  positions: { code: string; name: string }[];
}

/** 315/80 R22.5 duals, a 385/65 steer or super single, and the 215/75 R17.5 a lowbed rides on. */
const DUAL = { radius: 0.525, width: 0.295, gap: 0.045 };
const STEER = { radius: 0.535, width: 0.315, gap: 0 };
const SUPER_SINGLE = { radius: 0.535, width: 0.385, gap: 0 };
const SMALL = { radius: 0.39, width: 0.215 };
/** Half of a 2.5 m legal width at the outer tyre face. */
const OUTER = 1.255;

const FIRST_AXLE_DEPTH = 1.32;
const STEER_PITCH = 1.9;
const REAR_PITCH = 1.36;
const TRAILER_PITCH = 1.31;
const TRAILER_FIRST_TURNTABLE_DEPTH = 1.7;
const TRAILER_LAST_AXLE_FROM_TAIL = 1.45;
const MIN_TRUCK_LENGTH = 4.6;

function tyreSize(kind: TyreLayoutSubjectKind, axle: VehicleAxleInput, hasSmallWheels: boolean): TyreSize {
  const count = axle.positions.length;
  if (hasSmallWheels || count > 4) return { ...SMALL, gap: count > 4 ? 0.03 : 0.04, outerHalfWidth: OUTER };
  if (count >= 3) return { ...DUAL, outerHalfWidth: OUTER };
  if (axle.role === 'steer') return { ...STEER, outerHalfWidth: 1.02 + STEER.width / 2 };
  if (kind === 'trailer_type') return { ...SUPER_SINGLE, outerHalfWidth: OUTER - 0.02 };
  return { ...DUAL, gap: 0, outerHalfWidth: OUTER - 0.12 };
}

/**
 * Where one axle's positions land. Slot order is left to right, as the layout
 * stores it: the first half hang off the left wheel end and the last half off
 * the right, each side packed from the outside in, so four positions read as
 * two duals (LO LI · RI RO). An odd one out sits on the centreline.
 */
export function placeTyres(positions: { code: string; name: string }[], size: TyreSize): PlacedTyre[] {
  const count = positions.length;
  const perSide = Math.floor(count / 2);
  const step = size.width + size.gap;
  const outerCentre = size.outerHalfWidth - size.width / 2;
  const tyre = (p: { code: string; name: string }, x: number, side: PlacedTyre['side'], isOuter: boolean): PlacedTyre => ({
    ...p,
    x,
    side,
    isOuter,
    radius: size.radius,
    width: size.width,
  });
  const left = positions.slice(0, perSide).map((p, k) => tyre(p, -outerCentre + k * step, 'left', k === 0));
  const middle = count % 2 === 1 ? [tyre(positions[perSide], 0, 'middle', true)] : [];
  const right = positions.slice(count - perSide).map((p, j) => tyre(p, outerCentre - (perSide - 1 - j) * step, 'right', j === perSide - 1));
  return [...left, ...middle, ...right];
}

function pitchAfter(previous: TyreLayoutAxleRole, role: TyreLayoutAxleRole, wheelbase: number): number {
  if (previous !== 'steer') return REAR_PITCH;
  return role === 'steer' ? STEER_PITCH : wheelbase;
}

/** Steer axles 1.9 m apart, then the body's wheelbase to the first rear axle, then rear axles 1.36 m apart. */
export function truckAxleDepths(roles: TyreLayoutAxleRole[], style: TruckBodyStyle): number[] {
  const dims = TRUCK_STYLE_DIMENSIONS[style];
  const rearCount = roles.filter((role) => role !== 'steer').length;
  const wheelbase = rearCount > 1 ? dims.wheelbaseTandem : dims.wheelbaseSingle;
  const depths: number[] = [];
  roles.forEach((role, i) => {
    if (i === 0) return depths.push(FIRST_AXLE_DEPTH);
    return depths.push(depths[i - 1] + pitchAfter(roles[i - 1], role, wheelbase));
  });
  return depths;
}

/** A drawbar's turntable axles from the front; every other axle in one group ending 1.45 m from the tail. */
export function trailerAxleDepths(roles: TyreLayoutAxleRole[], length: number): number[] {
  const front = roles.filter((role) => role === 'turntable').length;
  const rear = roles.length - front;
  const last = length - TRAILER_LAST_AXLE_FROM_TAIL;
  let turntableSeen = 0;
  let rearSeen = 0;
  return roles.map((role) => {
    if (role === 'turntable') return TRAILER_FIRST_TURNTABLE_DEPTH + turntableSeen++ * TRAILER_PITCH;
    return last - (rear - 1 - rearSeen++) * TRAILER_PITCH;
  });
}

/**
 * The vehicle's axles and tyres in metres (gitaumoses4/chukua#176 follow-up):
 * pure placement from the body style and the axle list, which the 3D model
 * and the plan chart both draw. A truck is as long as its axles need; a
 * trailer is its style's length.
 */
export function placeVehicle(kind: TyreLayoutSubjectKind, bodyStyle: VehicleBodyStyle, axles: VehicleAxleInput[]): VehicleLayout {
  const roles = axles.map((axle) => axle.role);
  const isTruck = kind === 'truck_class' && isTruckBodyStyle(bodyStyle);
  const trailerDims = isTruck ? null : TRAILER_STYLE_DIMENSIONS[bodyStyle as TrailerBodyStyle];
  const truckLength = (depths: number[]) => Math.max((depths.at(-1) ?? 0) + TRUCK_STYLE_DIMENSIONS[bodyStyle as TruckBodyStyle].overhang, MIN_TRUCK_LENGTH);
  const length = trailerDims ? trailerDims.length : 0;
  const depths = isTruck ? truckAxleDepths(roles, bodyStyle) : trailerAxleDepths(roles, length);
  const placed = axles.map((axle, i) => {
    const size = tyreSize(kind, axle, trailerDims?.hasSmallWheels === true);
    return { role: axle.role, depth: depths[i], size, tyres: placeTyres(axle.positions, size) };
  });
  return { kind, bodyStyle, length: isTruck ? truckLength(depths) : length, axles: placed };
}
